import { useRef, useState } from 'react'
import { useSendSupportRequest } from '../../api/queries'
import { SUPPORT_TOPICS, type SupportTopic } from '../../api/types'
import { useAuthenticatedSession } from '../../auth/session'
import { useT, type MessageKey, type Translate } from '../../i18n'
import { Button, EmptyState, SelectField, TextAreaField, useToast } from '../../ui'
import screen from '../screen.module.css'
import styles from './support.module.css'
import { apiErrorText } from '../../bff/errorText'

/**
 * The support request form (biller users only).
 *
 * Internal staff are refused — by the API with a 403, and here with a plain
 * refusal screen — because the form is the biller-facing door to Maviance
 * support, not a general contact form.
 *
 * The validation below mirrors the API's limits (3 files, 2 MiB each, a
 * closed format list) so a user learns about an oversized screenshot when
 * they pick it, not after typing their whole message and submitting. The
 * server checks everything again — including the file CONTENT, which a
 * browser cannot be trusted to report honestly.
 *
 * The message box deliberately carries NO `maxLength` (MPAY-32085). The
 * attribute enforces the limit by silently discarding whatever does not fit,
 * which is invisible on a paste: a biller pasting a long problem description
 * keeps 5000 characters, loses the rest without a word, and sends a request
 * that stops mid-sentence. Refusing to send is recoverable; deleting somebody's
 * text is not. So the field holds everything, says where the limit falls, and
 * leaves the shortening to the person who wrote it.
 */

const MAX_FILES = 3
const MAX_FILE_BYTES = 2 * 1024 * 1024
const MAX_MESSAGE_CHARS = 5000

/**
 * The message length as the API measures it: runes of the TRIMMED message
 * (`len([]rune(strings.TrimSpace(...)))`).
 *
 * Both halves matter. `value.length` counts UTF-16 code units, so an emoji
 * counts twice in the browser and once in Go — count that way and a user is
 * refused a message the server would have accepted. And the API trims before
 * measuring, so trailing newlines must not eat into anyone's allowance.
 */
const messageLength = (value: string): number => [...value.trim()].length

/** Mirrors the API's extension allowlist. The `accept` attribute is built
 *  from it so the file picker starts in the right place, but `accept` is a
 *  hint the user can override — validateFiles is what actually refuses. */
const ALLOWED_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.pdf', '.txt', '.csv', '.xlsx']

const topicLabelKey = (topic: SupportTopic): MessageKey =>
  `support.topic.${topic}` as MessageKey

function validateFiles(files: readonly File[], t: Translate): string | null {
  if (files.length > MAX_FILES) return t('support.tooManyFiles', { max: String(MAX_FILES) })
  for (const file of files) {
    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase()
    if (!ALLOWED_EXTENSIONS.includes(ext)) return t('support.badFormat', { name: file.name })
    if (file.size > MAX_FILE_BYTES) return t('support.fileTooLarge', { name: file.name })
    if (file.size === 0) return t('support.fileEmpty', { name: file.name })
  }
  return null
}

export function Support() {
  const t = useT()
  const toast = useToast()
  const { user, businessTime } = useAuthenticatedSession()
  const send = useSendSupportRequest()

  const [topic, setTopic] = useState<SupportTopic>('OTHER')
  const [message, setMessage] = useState('')
  const [files, setFiles] = useState<readonly File[]>([])
  const [error, setError] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  if (user.isInternal) {
    return <EmptyState glyph="⊘" title={t('support.internalTitle')} body={t('support.internalBody')} />
  }

  const used = messageLength(message)
  const over = used - MAX_MESSAGE_CHARS
  // Static while the user edits: the changing number lives in the counter, and
  // an `role="alert"` whose text moves on every keystroke is announced on every
  // keystroke.
  const tooLong = over > 0 ? t('support.messageTooLong', { max: businessTime.number(MAX_MESSAGE_CHARS) }) : undefined
  const counter =
    over > 0
      ? t('support.charsOver', { count: businessTime.number(over) })
      : t('support.charsRemaining', { count: businessTime.number(-over) })

  const onPickFiles = (picked: FileList | null) => {
    if (!picked) return
    // Additive: picking again adds to the selection rather than replacing it,
    // because a native file input always replaces and losing two attachments
    // by adding a third is the surprise nobody wants.
    const next = [...files, ...Array.from(picked)]
    const problem = validateFiles(next, t)
    if (problem) {
      setError(problem)
    } else {
      setError(null)
      setFiles(next)
    }
    // Reset so picking the same file twice re-fires onChange.
    if (fileInput.current) fileInput.current.value = ''
  }

  const submit = async () => {
    if (!message.trim()) {
      setError(t('support.messageRequired'))
      return
    }
    if (tooLong) {
      // Belt and braces with the disabled button: a keyboard or scripted submit
      // must not slip past, and the API would refuse it with a 400 anyway.
      setError(tooLong)
      return
    }
    const problem = validateFiles(files, t)
    if (problem) {
      setError(problem)
      return
    }
    setError(null)
    try {
      await send.mutateAsync({ topic, message: message.trim(), attachments: files })
      toast.notify('success', t('support.sent'))
      setMessage('')
      setFiles([])
      setTopic('OTHER')
    } catch (err) {
      setError(apiErrorText(t, err).text)
    }
  }

  return (
    <section>
      <header className={screen.header}>
        <div>
          <h1 className={screen.title}>{t('support.title')}</h1>
          <p className={screen.subtitle}>{t('support.subtitle')}</p>
        </div>
      </header>

      <div className={styles.formCard}>
        <div className={screen.form}>
          <SelectField
            label={t('support.topic')}
            description={t('support.topicHint')}
            value={topic}
            onChange={(e) => setTopic(e.target.value as SupportTopic)}
          >
            {SUPPORT_TOPICS.map((key) => (
              <option key={key} value={key}>
                {t(topicLabelKey(key))}
              </option>
            ))}
          </SelectField>

          <div>
            <TextAreaField
              label={t('support.message')}
              description={t('support.messageHint')}
              value={message}
              error={tooLong}
              required
              rows={8}
              onChange={(e) => setMessage(e.target.value)}
            />
            {/* Not a live region: it changes on every keystroke, and a screen
                reader reading a running count out loud is unusable. The part
                that must be announced — that the message can no longer be
                sent — is the field's own error, which is. */}
            <p className={styles.counter}>{counter}</p>
          </div>

          <div>
            <input
              ref={fileInput}
              type="file"
              multiple
              accept={ALLOWED_EXTENSIONS.join(',')}
              className="sr-only"
              aria-label={t('support.attachments')}
              onChange={(e) => onPickFiles(e.target.files)}
            />
            <Button
              variant="secondary"
              size="sm"
              disabled={files.length >= MAX_FILES}
              onClick={() => fileInput.current?.click()}
            >
              {t('support.addAttachment')}
            </Button>
            <p className={screen.hint}>{t('support.attachmentHint')}</p>

            {files.length > 0 && (
              <ul className={styles.fileList}>
                {files.map((file, index) => (
                  <li key={`${file.name}-${index}`} className={styles.fileItem}>
                    <span className={styles.fileName}>{file.name}</span>
                    <span className={styles.fileSize}>{formatSize(file.size)}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFiles(files.filter((_, i) => i !== index))}
                    >
                      {t('support.removeAttachment')}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {error && (
            <p className={screen.warning} role="alert">
              {error}
            </p>
          )}

          <div>
            {/* Disabled with the reason stated next to the field, as the
                attachment button is when three files are already picked. */}
            <Button onClick={submit} loading={send.isPending} disabled={send.isPending || over > 0}>
              {t('support.send')}
            </Button>
          </div>
        </div>
      </div>
    </section>
  )
}

/** Human-readable size — support attachments are small by rule, so KB/MB is
 *  the whole range this ever renders. */
function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}
